import React from "react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/seo/SEO";

interface LegalDocumentSection {
  body: string[];
  title: string;
}

interface LegalDocumentPageProps {
  canonical: string;
  sections: LegalDocumentSection[];
  summary: string;
  title: string;
  version: string;
}

export const LEGAL_DOCUMENT_LAST_UPDATED = "May 27, 2026";
const LEGAL_REVIEW_NOTICE =
  "Launch blocker: this page is review-draft legal copy and is not approved for free-only pilot, paid pilot, self-service registration, or production use.";
const LEGAL_APPROVAL_NOTICE =
  "No counsel-reviewed final legal text or candidate-matched legal approval artifact was found in this repository. Counsel and product must approve this exact version before launch.";

export const LegalDocumentPage: React.FC<LegalDocumentPageProps> = ({
  canonical,
  sections,
  summary,
  title,
  version,
}) => {
  const pageTitle = `${title} | TerraMatrix`;

  return (
    <div className="min-h-screen bg-muted/30 text-foreground">
      <SEO
        title={pageTitle}
        description={summary}
        canonical={canonical}
        noindex={true}
      />

      <main className="mx-auto flex max-w-4xl flex-col gap-8 px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          <Link
            to="/register"
            className="font-medium text-primary underline underline-offset-2"
          >
            Back to registration
          </Link>
          <span aria-hidden="true">/</span>
          <Link
            to="/"
            className="font-medium text-primary underline underline-offset-2"
          >
            Home
          </Link>
        </div>

        <section className="rounded-2xl border border-border bg-background p-6 shadow-sm sm:p-8">
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
                TerraMatrix Legal
              </p>
              <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
                {title}
              </h1>
              <p className="max-w-3xl text-base leading-7 text-muted-foreground">
                {summary}
              </p>
            </div>

            <dl className="grid gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 sm:grid-cols-3">
              <div>
                <dt className="font-semibold">Version</dt>
                <dd>{version}</dd>
              </div>
              <div>
                <dt className="font-semibold">Last updated</dt>
                <dd>{LEGAL_DOCUMENT_LAST_UPDATED}</dd>
              </div>
              <div>
                <dt className="font-semibold">Launch status</dt>
                <dd>{LEGAL_REVIEW_NOTICE}</dd>
              </div>
            </dl>

            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-950">
              <p className="font-semibold">Legal approval missing</p>
              <p className="mt-2">{LEGAL_APPROVAL_NOTICE}</p>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-border bg-background p-6 shadow-sm sm:p-8">
          <div className="space-y-6">
            {sections.map((section) => (
              <section key={section.title} className="space-y-3">
                <h2 className="text-xl font-semibold tracking-tight">
                  {section.title}
                </h2>
                {section.body.map((paragraph) => (
                  <p
                    key={paragraph}
                    className="text-sm leading-7 text-muted-foreground sm:text-base"
                  >
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};
